<template>
  <form>
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col cols="4" sm="4" md="3" lg="3" class="py-0">
            <v-subheader>Type <span class="red--text">*</span></v-subheader>
          </v-col>
          <v-col cols="8" sm="8" md="4" lg="4" class="py-0">
            <v-radio-group v-model="form.type" row dense class="mt-0">
              <v-radio label="Per Employee" value="employee"></v-radio>
              <v-radio label="Per Department" value="department"></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="form.type === 'employee'">
          <v-col cols="4" sm="4" md="3" lg="3" class="py-0">
            <v-subheader>Employee <span class="red--text">*</span></v-subheader>
          </v-col>
          <v-col cols="8" sm="8" md="4" lg="4" class="py-0">
            <v-autocomplete
              v-model="form.employeeId"
              :error-messages="employeeIdErrors"
              required
              outlined
              dense
              flat
              item-text="name"
              item-value="hrId"
              background-color="white"
              @input="$v.form.employeeId.$touch()"
              @blur="$v.form.employeeId.$touch()"
              @change="onChangeEmployee"
              ref="employeeId"
              :items="employees"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row v-if="form.type === 'department'">
          <v-col cols="4" sm="4" md="3" lg="3" class="py-0">
            <v-subheader>Department <span class="red--text">*</span></v-subheader>
          </v-col>
          <v-col cols="8" sm="8" md="4" lg="4" class="py-0">
            <v-autocomplete
              v-model="form.departmentId"
              :error-messages="departmentIdErrors"
              required
              outlined
              dense
              flat
              item-text="name"
              item-value="id"
              background-color="white"
              @input="$v.form.departmentId.$touch()"
              @blur="$v.form.departmentId.$touch()"
              @change="onChangeDepartment"
              ref="departmentId"
              :items="departments"
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4" sm="4" md="3" lg="3" class="py-0">
            <v-subheader>Amount <span class="red--text">*</span></v-subheader>
          </v-col>
          <v-col cols="8" sm="8" md="4" lg="4" class="py-0">
            <v-text-field
              v-model.number="form.amount"
              :error-messages="amountErrors"
              required
              outlined
              dense
              flat
              type="number"
              background-color="white"
              @input="$v.form.amount.$touch()"
              @blur="$v.form.amount.$touch()"
              ref="amount"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4" sm="4" md="3" lg="3" class="py-0">
            <v-subheader>Threshold <span class="red--text">*</span></v-subheader>
          </v-col>
          <v-col cols="8" sm="8" md="4" lg="4" class="py-0">
            <v-text-field
              v-model.number="form.threshold"
              :error-messages="thresholdErrors"
              required
              outlined
              dense
              flat
              type="number"
              background-color="white"
              @input="$v.form.threshold.$touch()"
              @blur="$v.form.threshold.$touch()"
              ref="threshold"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-0" cols="12">
        <v-btn
          v-if="$rbac.can('setting-employee-allowance.create')"
          class="mr-4"
          @click="submit"
          color="primary"
        >
          submit
        </v-btn>
        <v-btn @click="clear">
          clear
        </v-btn>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, minValue, requiredIf } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
  mixins: [validationMixin],

  validations: {
    form: {
      employeeId: {
        required: requiredIf(function() {
          return this.form.type === "employee";
        }),
      },
      departmentId: {
        required: requiredIf(function() {
          return this.form.type === "department";
        }),
      },
      amount: { required, minValue: minValue(0) },
      threshold: { required, minValue: minValue(0) },
    },
  },

  data: () => ({
    departments: [],
    defaultForm: {
      type: "employee",
      employeeId: null,
      employeeName: "",
      departmentId: null,
      departmentName: "",
      amount: null,
      threshold: null,
    },
    form: {},
  }),

  computed: {
    employeeIdErrors() {
      const errors = [];
      if (!this.$v.form.employeeId.$dirty) return errors;
      !this.$v.form.employeeId.required && errors.push("Employee is required");
      return errors;
    },
    departmentIdErrors() {
      const errors = [];
      if (!this.$v.form.departmentId.$dirty) return errors;
      !this.$v.form.departmentId.required && errors.push("Department is required");
      return errors;
    },
    amountErrors() {
      const errors = [];
      if (!this.$v.form.amount.$dirty) return errors;
      !this.$v.form.amount.required && errors.push("Amount is required");
      !this.$v.form.amount.minValue && errors.push("Amount must be greater than or equal to 0");
      return errors;
    },
    thresholdErrors() {
      const errors = [];
      if (!this.$v.form.threshold.$dirty) return errors;
      !this.$v.form.threshold.required && errors.push("Threshold is required");
      !this.$v.form.threshold.minValue &&
        errors.push("Threshold must be greater than or equal to 0");
      return errors;
    },
    ...mapState("external", ["employees"]),
  },

  watch: {
    "form.type"() {
      this.$v.form.$reset();
      this.form.employeeId = null;
      this.form.employeeName = "";
      this.form.departmentId = null;
      this.form.departmentName = "";
    },
  },

  methods: {
    onChangeEmployee(val) {
      if (val) {
        const employee = this.employees.find(e => e.hrId === val);
        if (employee && employee.department) {
          this.form.employeeName = employee.name;
          this.form.departmentId = employee.department.id;
          this.form.departmentName = employee.department.name;
        }
      }
    },
    onChangeDepartment(val) {
      if (val) {
        const department = this.departments.find(d => d.id === val);
        if (department) {
          this.form.departmentName = department.name;
        }
      }
    },
    submit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        for (let key in Object.keys(this.$v.form)) {
          const input = Object.keys(this.$v.form)[key];
          if (input.includes("$")) return false;
          if (this.$v.form[input].$error) {
            if (this.$refs[input]) {
              this.$refs[input].focus();
            }
            break;
          }
        }
      } else {
        if (this.form.type === "employee") {
          this.$store.dispatch("employeeAllowance/create", {
            employeeId: this.form.employeeId,
            employeeName: this.form.employeeName,
            departmentId: this.form.departmentId,
            departmentName: this.form.departmentName,
            amount: this.form.amount,
            threshold: this.form.threshold,
          });
        } else {
          this.$store.dispatch("employeeAllowance/createByDepartment", {
            departmentId: this.form.departmentId,
            departmentName: this.form.departmentName,
            amount: this.form.amount,
            threshold: this.form.threshold,
          });
        }
      }
    },
    clear() {
      this.$v.form.$reset();
      this.form = Object.assign({}, this.defaultForm);
    },
  },

  mounted() {
    this.$store.dispatch("external/getEmployees").then(() => {
      const deptMap = {};
      this.employees.forEach(emp => {
        if (emp.department && !deptMap[emp.department.id]) {
          deptMap[emp.department.id] = emp.department;
        }
      });
      this.departments = Object.values(deptMap);
    });
    this.form = Object.assign({}, this.defaultForm);
  },
};
</script>

<style></style>
